import { useMemo } from 'react';
import type {
  EntityDecision,
  EntityDecisions,
  PreviewStatement,
  PreviewTag,
  PropertyValues,
  ReconcileCandidate,
  ReconcileItem,
  SheetRow,
  SpreadsheetPreviewResponse,
  ValuesHeld,
} from '@/lib/api';
import {
  buildItemIndex,
  className,
  decisionFor,
  groupStatements,
  plainWords,
  rollupColumns,
  unwrittenByColumn,
} from '@/lib/importValidation';
import { cn } from '@/lib/utils';
import { Message, messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

/**
 * Validation: what the import will store, read before it is stored. Each
 * thing is shown by its name and class with its facts, each fact once however
 * many rows state it. Nothing here holds the import: a conflict with the
 * ontology and a thing with several values are said, explained and left to
 * the person (ruling 4.4, ingestion is never gated on cleansing).
 *
 * A pure view over the preview and matching DTOs plus callbacks: the T1
 * wizard drives it today, and the T3 assisted review presents the same
 * component with its decisions pre-filled.
 */

const rowsText = (n: number) => `on ${n.toLocaleString()} row${n === 1 ? '' : 's'}`;

const candidateText = (cand: ReconcileCandidate, expectedClass: string | null): string =>
  `${cand.label ?? cand.uri} · ${cand.classes.length > 0 ? cand.classes.join(', ') : cand.entity_type}${
    cand.graph ? ` · ${cand.graph}` : ''
  } (${cand.score.toFixed(2)}${cand.match_kind !== 'label' ? `, ${cand.match_kind}` : ''}${
    !cand.class_match && expectedClass ? `, not ${expectedClass}` : ''
  })`;

/** The decision picker: use an existing thing, add as new, or leave out. */
function DecisionPicker({
  item,
  decision,
  scope,
  disabled,
  onDecide,
}: {
  item: ReconcileItem;
  decision: EntityDecision;
  /** What leaving out removes: a whole thing, or one value's facts. */
  scope: 'record' | 'value';
  disabled: boolean;
  onDecide: (mintedIri: string, decision: EntityDecision) => void;
}) {
  const value =
    decision.action === 'link' ? decision.uri : decision.action === 'ignore' ? '__ignore__' : '__new__';
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        onDecide(
          item.minted_iri,
          v === '__new__'
            ? { action: 'create_new' }
            : v === '__ignore__'
              ? { action: 'ignore' }
              : { action: 'link', uri: v },
        );
      }}
      className="max-w-full rounded-md border border-input bg-background px-2 py-1 font-sans text-xs"
      title={`"${item.value}": use a stored entity, add as new, or leave out`}
    >
      {item.candidates.map((cand) => (
        <option key={cand.uri} value={cand.uri}>
          use: {candidateText(cand, item.expected_class)}
        </option>
      ))}
      <option value="__new__">
        add as new{item.expected_class ? ` ${item.expected_class}` : ''}
      </option>
      <option value="__ignore__">
        {scope === 'record' ? 'leave out: this entity and its statements' : 'leave out: no statement'}
      </option>
    </select>
  );
}

/** A tag as a reader meets it: a context reads "in <the thing's name>". */
const tagText = (t: PreviewTag): string =>
  t.name === '@context' ? `in ${t.label ?? t.value}` : `${t.name.replace(/^@/, '')}: ${t.label ?? t.value}`;

/** One thing's values for a property, by name:
 *  "Adam Smith: 24 (on 20 rows), 42 (on 17 rows)". */
function HeldLine({
  held,
  onLeaveOut,
}: {
  held: ValuesHeld;
  onLeaveOut?: ((statements: PreviewStatement[]) => void) | undefined;
}) {
  const who = held.subject_label ?? held.subject;
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="font-medium">{who}</span>
      {held.context && <span className="text-muted-foreground">in {held.context}</span>}
      {held.values.map((v) => (
        <span key={v.statement_id} className="inline-flex items-center gap-1">
          <span className="font-mono">{v.label ?? v.value}</span>
          <span className="text-muted-foreground">({rowsText(v.rows)})</span>
          {onLeaveOut && (
            <button
              type="button"
              aria-label={`Leave out ${v.label ?? v.value} for ${who}`}
              title="This value is not imported. You can put it back from the list of what you left out."
              onClick={() =>
                onLeaveOut([
                  {
                    id: v.statement_id,
                    subject: held.subject,
                    ...(held.subject_label ? { subject_label: held.subject_label } : {}),
                    predicate: held.predicate,
                    object: v.value,
                    ...(v.label ? { object_label: v.label } : {}),
                    object_is_iri: v.label !== undefined,
                    row: 0,
                    stated_on: v.rows,
                    subject_rows: 0,
                    column: null,
                    tags: [],
                  },
                ])
              }
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Leave out
            </button>
          )}
        </span>
      ))}
    </li>
  );
}

const andMore = (p: PropertyValues) =>
  p.things > p.examples.length ? (
    <li className="text-muted-foreground">and {(p.things - p.examples.length).toLocaleString()} more</li>
  ) : null;

export function ValidationStep({
  preview,
  rows,
  reconcileItems,
  decisions,
  leftOut,
  columnOfPredicate,
  previewOffset,
  busy,
  onDecide,
  onLeaveOut,
  onPutBack,
  onRecheck,
  onPage,
}: {
  preview: SpreadsheetPreviewResponse;
  /** The data rows, so each statement is shown beside the cell it was read from. */
  rows: SheetRow[];
  reconcileItems: ReconcileItem[];
  decisions: EntityDecisions;
  /** Facts the person left out, kept here so each can be put back; the server
   *  leaves them out of every preview and of the import. */
  leftOut: PreviewStatement[];
  /** Which sheet column writes which property, from the mapping. */
  columnOfPredicate: Map<string, string>;
  previewOffset: number;
  busy: boolean;
  onDecide: (mintedIri: string, decision: EntityDecision) => void;
  onLeaveOut: (statements: PreviewStatement[]) => void;
  onPutBack: (statementIds: string[]) => void;
  onRecheck: () => void;
  onPage: (offset: number) => void;
}) {
  const itemIndex = useMemo(
    () => buildItemIndex(reconcileItems, decisions),
    [reconcileItems, decisions],
  );
  const groups = useMemo(() => groupStatements(preview.statements), [preview.statements]);
  // The cell a statement was read from, when its row is at hand: a folder's
  // pages carry a sheet's first rows only.
  const cellOf = (s: PreviewStatement) => (s.column ? rows[s.row - 1]?.[s.column] : undefined);
  const rollups = useMemo(() => rollupColumns(reconcileItems, decisions), [reconcileItems, decisions]);
  const linkedCount = Object.values(decisions).filter((d) => d.action === 'link').length;
  const report = preview.report;

  return (
    <div className={cn('space-y-3', busy && 'pointer-events-none opacity-60')}>
      <div>
        <h3 className="text-sm font-semibold">Validation — the statements the import will store</h3>
        <p className="text-xs text-muted-foreground">
          Each entity is listed once, by its label and class, with its statements: property, object, and the
          cell each was read from. A statement several rows make is one statement, shown with how many rows
          make it. Leave out any statement you do not want. Nothing is written until you import.
        </p>
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Rows read</dt>
        <dd>{report.rows_in.toLocaleString()}</dd>
        <dt className="text-muted-foreground">Statements to store</dt>
        <dd>{preview.total.toLocaleString()}</dd>
        <dt className="text-muted-foreground">New entities</dt>
        <dd>{report.entities_minted.toLocaleString()}</dd>
        <dt className="text-muted-foreground">Entities matched to stored entities</dt>
        <dd>{report.entities_reconciled.toLocaleString()}</dd>
        <dt className="text-muted-foreground">ObjectProperty statements</dt>
        <dd>{(report.relations_distinct ?? report.relations_out).toLocaleString()}</dd>
        {report.statements_ignored > 0 && (
          <>
            <dt className="text-muted-foreground">Left out by you</dt>
            <dd>{report.statements_ignored.toLocaleString()}</dd>
          </>
        )}
        {report.skipped.length > 0 && (
          <>
            <dt className="text-muted-foreground">Rows not read</dt>
            <dd>{report.skipped.length.toLocaleString()}</dd>
          </>
        )}
        {report.references_dropped > 0 && (
          <>
            <dt className="text-muted-foreground">References with no match, left out</dt>
            <dd>{report.references_dropped.toLocaleString()}</dd>
          </>
        )}
      </dl>

      {/* Per-column matching rollup — the column-level view before the detail. */}
      {rollups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">
            Matches: {linkedCount} of {reconcileItems.length} use a stored entity.
          </span>
          {rollups.map((r) => (
            <span key={r.column} className="rounded border border-border px-1.5 py-0.5">
              <span className="font-mono">{r.column}</span>: {r.linked} matched · {r.created} new
              {r.ignored > 0 ? ` · ${r.ignored} left out` : ''}
            </span>
          ))}
          <button
            type="button"
            onClick={onRecheck}
            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
          >
            Look for matches again
          </button>
        </div>
      )}

      {/* A conflict needs the ontology to say a property holds fewer values
          than the sheet gives. It is said in red because something disagrees
          with a declaration; it does not hold the import. */}
      {preview.conflicts.map((p) => {
        const max = p.declared_max ?? 1;
        const what = plainWords(p.predicate);
        return (
          <Message key={`${p.predicate} ${p.subject_class ?? ''}`} kind="warning">
            <p className="text-xs font-semibold">
              Your ontology says a {className(p.subject_class)} holds at most {max} {what}. The sheet gives{' '}
              {p.things.toLocaleString()} of them more.
            </p>
            <ul className="mt-1 space-y-1 text-xs">
              {p.examples.map((e) => (
                <HeldLine key={`${e.subject} ${e.context ?? ''}`} held={e} onLeaveOut={onLeaveOut} />
              ))}
              {andMore(p)}
            </ul>
            <p className="mt-1 text-xs">
              Every value is stored unless you leave it out, and the import does not wait for this. If the
              values are right and the rule is not, change the rule on the Ontology page: it is the{' '}
              <span className="font-mono">@maxCardinality</span> on{' '}
              <span className="font-mono">{p.predicate}</span>.
            </p>
          </Message>
        );
      })}

      {/* Several values where nothing says a property holds one: information,
          with what to do if the value belongs to something else on the row. */}
      {preview.several_values.length > 0 && (
        <details open className={messageClass('guidance', 'normal')}>
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold">
            Some entities have more than one object for a property. Nothing is wrong, and every object is
            stored.
          </summary>
          <div className="space-y-2 px-3 pb-2 text-xs">
            <p>
              This happens when a value changes across the sheet, such as a player who wears one number in
              some games and another later. Nothing in your ontology says these properties hold only one
              object, so each object is kept as a statement.
            </p>
            {preview.several_values.map((p) => {
              const what = plainWords(p.predicate);
              const column = columnOfPredicate.get(p.predicate);
              return (
                <div key={`${p.predicate} ${p.subject_class ?? ''}`} className="space-y-1">
                  <p>
                    <span className="font-medium">
                      {what} of a {className(p.subject_class)}
                    </span>
                    : {p.things.toLocaleString()} {p.things === 1 ? 'has' : 'have'} more than one object.
                  </p>
                  <ul className="space-y-1 pl-3">
                    {p.examples.map((e) => (
                      <HeldLine key={`${e.subject} ${e.context ?? ''}`} held={e} />
                    ))}
                    {andMore(p)}
                  </ul>
                  <p className="text-muted-foreground">
                    If {what} belongs to something else on the row, such as the game, go Back to
                    page 2, Properties, and choose that column as the Context of{' '}
                    <span className="font-mono">{column ?? p.predicate}</span>. Each one then keeps its own
                    value, and nothing is left out.
                  </p>
                </div>
              );
            })}
            <p className="text-muted-foreground">
              To have a second value reported as a conflict, say in your ontology that the property holds
              one: add <span className="font-mono">@maxCardinality=1</span> to the statement that gives its
              domain.
            </p>
          </div>
        </details>
      )}

      {/* What the person left out: reversible, never silent. */}
      {leftOut.length > 0 && (
        <details className="rounded-md border border-border">
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-muted-foreground">
            Left out by you: {leftOut.length} statement{leftOut.length === 1 ? '' : 's'} will not be imported
          </summary>
          <ul className="space-y-1 px-3 pb-2 text-xs">
            {leftOut.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPutBack([s.id])}
                  className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                >
                  Put back
                </button>
                <span>
                  <span className="font-medium">{s.subject_label ?? s.subject}</span>{' '}
                  <span className="font-mono">{s.predicate}</span>{' '}
                  <span className="font-mono">{s.object_label ?? s.object}</span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Statements several rows make alike are stored once. A row names
          several things, so this is the ordinary case and reads as plain
          information: the rows are not repeats, and nothing is wrong. */}
      {preview.fidelity && preview.fidelity.collapsed > 0 && (
        <p className="text-xs text-muted-foreground">
          {preview.fidelity.distinct.toLocaleString()} distinct statements will be stored from{' '}
          {preview.fidelity.generated.toLocaleString()} read. An entity on several rows, such as a game on each
          of its players’ rows, makes the same statement on each; it is stored once.
        </p>
      )}

      {/* Advisory: matched things already hold different values in the store. */}
      {preview.store_conflicts.length > 0 && (
        <div className={messageClass('platform', 'compact', 'warning')}>
          <p className="font-semibold">
            Things already stored hold different values
            {preview.store_conflicts_truncated ? ' (first 50 shown)' : ''}
          </p>
          <p className="mt-1 text-muted-foreground">
            Both objects will be kept as separate statements. If the sheet should replace the stored object, edit
            the entity after import, or stop using the match in its group below.
          </p>
          <ul className="mt-1 space-y-0.5 font-mono">
            {preview.store_conflicts.slice(0, 50).map((c, i) => (
              <li key={i}>
                {c.subject} {c.predicate}: sheet "{c.sheet_value}", stored{' '}
                {c.stored_values.map((v) => `"${v}"`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Every row not read and every cell not written, never silent. */}
      {report.skipped.length > 0 && (
        <details open={report.skipped.length <= 10} className={messageClass('platform', 'normal', 'warning')}>
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-tres-warning">
            {report.skipped.length} row{report.skipped.length === 1 ? ' was' : 's were'} not read
          </summary>
          <div className="max-h-48 overflow-auto px-3 pb-2">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">Row</th>
                  <th className="py-1 font-medium">Why</th>
                </tr>
              </thead>
              <tbody>
                {report.skipped.map((s) => (
                  <tr key={s.row} className="border-t border-border">
                    <td className="py-1 pr-3 tabular-nums">{s.row}</td>
                    <td className="py-1">{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {report.values_rejected.length > 0 && (
        <div className={cn(messageClass('platform', 'normal', 'warning'), 'space-y-2')}>
          <p className="text-xs font-semibold">
            {report.values_rejected.length.toLocaleString()} cell
            {report.values_rejected.length === 1 ? ' is' : 's are'} not written: the text does not fit the datatype
            chosen for the column. Everything else on those rows is written.
          </p>
          <table className="tres-table">
            <thead>
              <tr>
                <th>Column</th>
                <th>Datatype chosen</th>
                <th>Cells not written</th>
                <th>Such as</th>
              </tr>
            </thead>
            <tbody>
              {unwrittenByColumn(report.values_rejected).map((c) => (
                <tr key={`${c.column}:${c.datatype}`} aria-label={`Not written from ${c.column}`}>
                  <td className="font-mono text-xs">{c.column}</td>
                  <td className="text-xs">{c.datatype}</td>
                  <td className="text-xs tabular-nums">{c.cells.toLocaleString()}</td>
                  <td className="font-mono text-xs">{c.examples.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs">
            To write them, go Back to page 2, Properties, and choose a datatype these values fit; xsd:string keeps
            any text as it is.
          </p>
        </div>
      )}

      {report.classes_created != null && report.classes_created.length > 0 && (
        <details
          open={report.classes_created.length <= 10}
          className={messageClass('platform', 'normal', 'warning')}
        >
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-tres-warning">
            {report.classes_created.length} new class
            {report.classes_created.length === 1 ? '' : 'es'} will be created from the sheet: the value
            matched no class your ontology has. Rename the value to match, or import and merge the new class
            into an existing one on the Ontology page.
          </summary>
          <div className="max-h-48 overflow-auto px-3 pb-2">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">Value</th>
                  <th className="py-1 pr-3 font-medium">New class</th>
                  <th className="py-1 pr-3 font-medium">Under</th>
                  <th className="py-1 font-medium">Rows</th>
                </tr>
              </thead>
              <tbody>
                {report.classes_created.map((c) => (
                  <tr key={c.class} className="border-t border-border">
                    <td className="py-1 pr-3">{c.label}</td>
                    <td className="py-1 pr-3 font-mono">{c.class}</td>
                    <td className="py-1 pr-3 font-mono">{c.parent}</td>
                    <td className="py-1 tabular-nums">{c.rows}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {report.references_created != null && report.references_created.length > 0 && (
        <div className="rounded-md border border-border px-3 py-2 text-xs">
          <p className="font-semibold">
            New entities this import creates: the cell matched no stored entity
          </p>
          <ul className="mt-1 space-y-1">
            {report.references_created.map((r) => (
              <li key={r.column}>
                <span className="font-mono">{r.column}</span>: {r.count} new{r.class ? ' ' : ''}
                {r.class && <span className="font-mono">{r.class}</span>} — {r.examples.join(', ')}
                {r.count > r.examples.length ? ', …' : ''}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-muted-foreground">
            A value with a near match offers it in its line below. To match more, correct the value in the
            sheet or choose the class its terms belong to. A code its list lacks is created in that list
            with its meaning pending.
          </p>
        </div>
      )}

      {report.codes_ambiguous != null && report.codes_ambiguous.length > 0 && (
        <div className={messageClass('platform', 'compact', 'warning')}>
          <p className="font-semibold text-tres-warning">
            Codes that name more than one term of their list are left out of the import. Give each term its
            own code in the list, then validate again.
          </p>
          <ul className="mt-1 space-y-1">
            {report.codes_ambiguous.map((a) => (
              <li key={`${a.column} ${a.code}`}>
                <span className="font-mono">{a.column}</span>: <span className="font-mono">{a.code}</span> in{' '}
                <span className="font-mono">{a.class}</span> names{' '}
                <span className="font-mono">{a.terms.join(', ')}</span>, {rowsText(a.rows)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The facts, grouped by the thing they are about. */}
      <div className="max-h-[32rem] space-y-2 overflow-auto rounded-md border border-border p-2">
        {groups.map((g) => {
          const subjectItem = itemIndex.get(g.subject);
          const linked = subjectItem && decisionFor(decisions, subjectItem).action === 'link';
          return (
            <section key={g.subject} aria-label={g.label ?? g.subject} className="rounded border border-border">
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-2 py-1.5 text-xs">
                <span className="font-medium">{g.label ?? g.subject}</span>
                {g.klass && <span className="text-muted-foreground">{className(g.klass)}</span>}
                {linked && (
                  <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-secondary-foreground">
                    already stored
                  </span>
                )}
                <span className="text-muted-foreground">{rowsText(g.rows)}</span>
                {g.label && <span className="font-mono text-[10px] text-muted-foreground">{g.subject}</span>}
                {subjectItem && (
                  <DecisionPicker
                    item={subjectItem}
                    decision={decisionFor(decisions, subjectItem)}
                    scope="record"
                    disabled={busy}
                    onDecide={onDecide}
                  />
                )}
              </div>
              <table className="w-full text-left text-xs">
                <tbody>
                  {g.statements.map((s) => {
                    const objectItem = s.object_is_iri ? itemIndex.get(s.object) : undefined;
                    return (
                      <tr key={s.id} className="border-t border-border/60 align-top">
                        <td className="w-24 px-2 py-1">
                          <button
                            type="button"
                            onClick={() => onLeaveOut([s])}
                            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                            title="This statement is not imported. You can put it back from the list of what you left out."
                          >
                            Leave out
                          </button>
                        </td>
                        <td className="w-56 px-2 py-1 font-mono text-muted-foreground">{s.predicate}</td>
                        <td className="px-2 py-1">
                          {objectItem ? (
                            <DecisionPicker
                              item={objectItem}
                              decision={decisionFor(decisions, objectItem)}
                              scope="value"
                              disabled={busy}
                              onDecide={onDecide}
                            />
                          ) : s.object_is_iri ? (
                            <span title={s.object}>{s.object_label ?? s.object}</span>
                          ) : (
                            <span className="font-mono">{s.object}</span>
                          )}
                          {s.tags.length > 0 && (
                            <span className="ml-2 text-tres-text-secondary">
                              {s.tags.map(tagText).join(' · ')}
                            </span>
                          )}
                        </td>
                        <td className="w-48 px-2 py-1 text-right text-muted-foreground">
                          {s.column && (
                            <span className="block truncate" title={`row ${s.row}, ${s.column}${cellOf(s) === undefined ? '' : `: ${cellOf(s)}`}`}>
                              <span className="font-mono">{s.column}</span>
                              {cellOf(s) === undefined ? '' : `: ${cellOf(s)}`}
                            </span>
                          )}
                          {s.stated_on > 1 ? rowsText(s.stated_on) : `row ${s.row}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      {(preview.truncated || previewOffset > 0) && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            Statements {(previewOffset + 1).toLocaleString()} to{' '}
            {(previewOffset + preview.displayed).toLocaleString()} of {preview.total.toLocaleString()}. All
            will be written on import.
          </span>
          <button
            type="button"
            disabled={busy || previewOffset === 0}
            onClick={() => onPage(Math.max(0, previewOffset - preview.displayed))}
            className="rounded border border-input px-2 py-0.5 hover:bg-muted disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={busy || previewOffset + preview.displayed >= preview.total}
            onClick={() => onPage(previewOffset + preview.displayed)}
            className="rounded border border-input px-2 py-0.5 hover:bg-muted disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
